import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { motion } from "framer-motion";
import { useLang } from "../context/LanguageContext";
import ProductCard from "../components/ProductCard";
import { fetchProducts } from "../lib/api";

const HERO_IMG = "/brand/hero-paris-india.jpg";

const Home = () => {
  const { t, lang } = useLang();
  const [products, setProducts] = useState([]);

  useEffect(() => {
    fetchProducts().then(setProducts).catch(() => setProducts([]));
  }, []);

  const featured = products.slice(0, 8);

  return (
    <div data-testid="page-home">
      {/* HERO */}
      <section
        className="relative pt-24 md:pt-28 pb-12 md:pb-20 overflow-hidden"
        data-testid="home-hero"
      >
        <div className="px-6 md:px-12 lg:px-24 grid lg:grid-cols-12 gap-10 items-end">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 z-10"
          >
            <p className="overline mb-6">{t.hero.overline}</p>
            <h1 className="font-serif-display font-light text-[2.75rem] leading-[1.05] sm:text-6xl lg:text-[5.5rem] tracking-tight text-[#1E2B3C]">
              {t.hero.title}
              <br />
              <span className="italic text-[#B34A36]">{t.hero.titleAccent}</span>
            </h1>
            <p className="mt-7 text-base md:text-lg text-[#5C5C5C] leading-relaxed max-w-xl">
              {t.hero.sub}
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <Link to="/shop" className="btn-primary" data-testid="hero-cta-shop">
                {t.hero.cta}
                <ArrowRight size={16} />
              </Link>
              <Link to="/story" className="btn-secondary" data-testid="hero-cta-story">
                {t.hero.cta2}
              </Link>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 1.02 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-6 relative"
          >
            <div className="relative aspect-[4/5] overflow-hidden grain bg-[#F2EFE9]">
              <img
                src={HERO_IMG}
                alt="Parisian interior with Indian textile"
                className="w-full h-full object-cover"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vintage train ticker removed */}

      {/* CATEGORIES BENTO */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32" data-testid="home-categories">
        <div className="flex items-end justify-between mb-12 md:mb-16 flex-wrap gap-4">
          <div>
            <p className="overline mb-3">{t.sections.categories}</p>
            <h2 className="font-serif-display text-4xl md:text-5xl font-light text-[#1E2B3C]">
              {lang === "fr" ? "Cinq univers, une main." : "Five universes, one hand."}
            </h2>
          </div>
          <Link to="/shop" className="link-underline text-sm tracking-[0.18em] uppercase">
            {t.common.shopAll} →
          </Link>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6">
          {/* Tile 1 — Spices (big editorial, top-left) */}
          <Link
            to="/spices"
            className="col-span-12 md:col-span-7 group relative overflow-hidden aspect-[16/11] md:aspect-auto md:min-h-[360px]"
            data-testid="cat-spices"
          >
            <img
              src="/brand/spice-cardamom.jpg"
              alt={t.cats.spices.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              style={{ mixBlendMode: "multiply" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/65 via-black/15 to-transparent" />
            <div className="absolute bottom-8 left-8 text-white max-w-md">
              <p className="overline text-[#D99A29]">01 · {lang === "fr" ? "Univers" : "Universe"}</p>
              <h3 className="font-serif-display text-4xl md:text-5xl font-light mt-2">{t.cats.spices.name}</h3>
              <p className="mt-3 text-white/80 text-sm md:text-base max-w-sm">{t.cats.spices.desc}</p>
            </div>
          </Link>

          {/* Tile 2 — Saffron (top-right) */}
          <Link
            to="/saffron"
            className="col-span-12 md:col-span-5 group relative overflow-hidden aspect-[4/3] md:aspect-auto md:min-h-[360px] bg-[#F2EFE9]"
            data-testid="cat-saffron"
          >
            <img
              src="/brand/saffron-hero.jpg"
              alt={t.cats.saffron.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              style={{ mixBlendMode: "multiply" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E2B3C]/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <p className="overline text-[#D99A29]">02</p>
              <h3 className="font-serif-display text-3xl md:text-4xl font-light mt-1">{t.cats.saffron.name}</h3>
              <p className="mt-2 text-white/85 text-sm max-w-xs">{t.cats.saffron.desc}</p>
            </div>
          </Link>

          {/* Tile 3 — Textiles (bottom-left) */}
          <Link
            to="/textiles"
            className="col-span-12 md:col-span-5 group relative overflow-hidden aspect-[4/3] md:aspect-auto md:min-h-[300px] bg-[#F2EFE9]"
            data-testid="cat-textiles"
          >
            <img
              src="/brand/textile-kalamkari.jpg"
              alt={t.cats.textiles.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              style={{ mixBlendMode: "multiply" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E2B3C]/65 via-[#1E2B3C]/15 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white">
              <p className="overline text-[#D99A29]">03</p>
              <h3 className="font-serif-display text-3xl md:text-4xl font-light mt-1">{t.cats.textiles.name}</h3>
              <p className="mt-2 text-white/85 text-sm max-w-xs">{t.cats.textiles.desc}</p>
            </div>
          </Link>

          {/* Tile 4 — Deco Art (bottom-right, wider) */}
          <Link
            to="/deco"
            className="col-span-12 md:col-span-7 group relative overflow-hidden aspect-[16/11] md:aspect-auto md:min-h-[300px] bg-[#F2EFE9]"
            data-testid="cat-deco"
          >
            <img
              src="/brand/deco-tanjore.jpg?v=2"
              alt={t.cats.deco.name}
              className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-105"
              style={{ mixBlendMode: "multiply" }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1E2B3C]/65 via-[#1E2B3C]/15 to-transparent" />
            <div className="absolute bottom-6 left-6 text-white max-w-md">
              <p className="overline text-[#D99A29]">04</p>
              <h3 className="font-serif-display text-3xl md:text-4xl font-light mt-1">{t.cats.deco.name}</h3>
              <p className="mt-2 text-white/85 text-sm max-w-sm">{t.cats.deco.desc}</p>
            </div>
          </Link>

          {/* Tile 5 — Essential Oils (full-width strip) */}
          <Link
            to="/oils"
            className="col-span-12 group relative overflow-hidden aspect-[16/6] md:aspect-auto md:min-h-[260px] bg-[#FAF9F6]"
            data-testid="cat-oils"
          >
            <img
              src="/brand/oil-mitti-attar.jpg"
              alt={t.cats.oils.name}
              className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-[1200ms] group-hover:scale-[1.03]"
              style={{ mixBlendMode: "multiply" }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#1E2B3C]/65 via-[#1E2B3C]/25 to-transparent" />
            <div className="absolute inset-y-0 left-0 flex items-center px-8 md:px-12 max-w-3xl">
              <div className="text-white">
                <p className="overline text-[#D99A29]">05 · {lang === "fr" ? "Nouveau" : "New"}</p>
                <h3 className="font-serif-display text-3xl md:text-5xl font-light mt-2 leading-[1.05]">
                  {t.cats.oils.name}
                </h3>
                <p className="mt-3 text-white/85 text-sm md:text-base max-w-xl">{t.cats.oils.desc}</p>
              </div>
            </div>
          </Link>
        </div>
      </section>

      {/* STORY BLOCK */}
      <section className="relative bg-[#F2EFE9] py-24 md:py-32 overflow-hidden" data-testid="home-story">
        <div className="relative px-6 md:px-12 lg:px-24 max-w-4xl mx-auto">
          <p className="overline mb-5">{t.storyBlock.overline}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl font-light leading-[1.05] text-[#1E2B3C]">
            {t.storyBlock.title}
          </h2>
          <p className="font-serif-display italic text-xl md:text-2xl text-[#B34A36] mt-4">
            {t.storyBlock.subtitle}
          </p>
          <div className="mt-8 space-y-5 text-[#1A1A1A]/85 leading-relaxed max-w-2xl">
            <p>{t.storyBlock.body1}</p>
            <p>{t.storyBlock.body2}</p>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12 md:mt-16"
          >
            <div className="aspect-[16/10] md:aspect-[16/9] overflow-hidden grain bg-[#F2EFE9]">
              <img
                src="/brand/artisan-hands.jpg?v=3"
                alt="Brown hands working on Madhubani painting"
                className="w-full h-full object-cover"
                style={{ mixBlendMode: "multiply" }}
              />
            </div>
          </motion.div>

          <Link to="/story" className="btn-secondary mt-10 md:mt-12" data-testid="home-story-cta">
            {t.storyBlock.learnMore}
            <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* SAFFRON TEASER */}
      <section
        className="relative overflow-hidden bg-[#1E2B3C] text-[#FAF9F6]"
        data-testid="home-saffron"
      >
        <div className="absolute inset-0">
          <img
            src="/brand/saffron-hero.jpg"
            alt=""
            className="w-full h-full object-cover opacity-40"
            style={{ mixBlendMode: "screen" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#1E2B3C]/95 via-[#1E2B3C]/70 to-[#B34A36]/40" />
        </div>
        <div className="relative px-6 md:px-12 lg:px-24 py-24 md:py-36 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="overline" style={{ color: "#D99A29" }}>
              {t.sections.saffron}
            </p>
            <h2 className="font-serif-display font-light text-5xl md:text-6xl lg:text-7xl leading-[1.02] mt-5">
              {t.saffron.title}{" "}
              <span className="italic text-[#D99A29]">{t.saffron.titleAccent}</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-white/80 leading-relaxed max-w-xl">
              {t.saffron.ledeShort}
            </p>
            <Link
              to="/saffron"
              className="btn-primary mt-9 inline-flex bg-[#D99A29] text-[#1E2B3C] hover:bg-white"
              data-testid="home-saffron-cta"
            >
              {t.saffron.ctaShop}
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="lg:col-span-5 lg:col-start-8 grid grid-cols-2 gap-3 md:gap-4">
            {[
              { k: "150 000", l: lang === "fr" ? "fleurs / kg" : "flowers / kg" },
              { k: "17", l: lang === "fr" ? "jours de floraison" : "bloom days" },
              { k: "5h-8h", l: lang === "fr" ? "fenêtre de cueillette" : "harvest window" },
              { k: "IGP", l: lang === "fr" ? "Kashmir Saffron 2020" : "Kashmir Saffron 2020" },
            ].map((s, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="border border-white/15 backdrop-blur-sm bg-white/5 p-6 md:p-7"
              >
                <p className="font-serif-display text-3xl md:text-4xl text-[#D99A29]">{s.k}</p>
                <p className="mt-1 text-xs tracking-[0.15em] uppercase text-white/70">{s.l}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Featured products section removed */}

      {/* VALUES */}
      <section className="bg-[#FAF9F6] text-[#1E2B3C] py-24 md:py-32 border-t border-[#E5E1D8]" data-testid="home-values">
        <div className="px-6 md:px-12 lg:px-24">
          <p className="overline text-[#B34A36] mb-4">{t.sections.values}</p>
          <h2 className="font-serif-display text-4xl md:text-5xl font-light max-w-2xl text-[#1E2B3C]">
            {lang === "fr"
              ? "Quatre promesses, gravées dans chaque pièce."
              : "Four promises, etched in every piece."}
          </h2>

          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-10 md:gap-14">
            {[t.values.v1, t.values.v2, t.values.v3, t.values.v4].map((v, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
                data-testid={`value-${i + 1}`}
              >
                <p className="font-serif-display italic text-3xl text-[#B34A36]">0{i + 1}</p>
                <h3 className="font-serif-display text-2xl mt-3 text-[#1E2B3C]">{v.title}</h3>
                <p className="mt-3 text-[#1A1A1A]/70 leading-relaxed text-sm">{v.body}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
